import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './CameraCapture.css';

// Prise de photo directe par la webcam, en alternative à l'upload de fichier
// (plus rapide en consultation : pas de transfert depuis le téléphone).
//
// ⚠️ AUCUN effet miroir, ni sur l'aperçu ni sur la capture. L'app juge des
// asymétries gauche/droite (convexité scoliotique, varus/valgus des talons,
// arche gauche vs droite) : une image en miroir -- le réflexe naturel pour un
// aperçu de caméra frontale -- inverserait le diagnostic. On préfère aussi la
// caméra arrière (facingMode 'environment'), celle qu'on utilise pour
// photographier un patient en face de soi.
export default function CameraCapture({ label, onCapture, onClose }) {
  const { t } = useTranslation();
  const videoRef  = useRef(null);
  const streamRef = useRef(null);

  const [devices,  setDevices]  = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [error,    setError]    = useState('');
  const [starting, setStarting] = useState(true);
  // Photo capturée en attente de validation : on ne remonte rien au parent
  // avant que le praticien ait confirmé, pour pouvoir reprendre un cliché raté
  // (patient mal cadré, flou) sans repasser par l'upload.
  const [shot, setShot] = useState(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async (wantedDeviceId) => {
    stopStream();
    setStarting(true);
    setError('');
    try {
      const constraints = {
        audio: false,
        video: wantedDeviceId
          ? { deviceId: { exact: wantedDeviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
          : { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      // La liste des caméras n'est étiquetée qu'après une autorisation
      // accordée -- on l'énumère donc APRÈS getUserMedia, pas avant.
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter(d => d.kind === 'videoinput');
      setDevices(cams);
      const activeId = stream.getVideoTracks()[0]?.getSettings?.().deviceId;
      if (activeId) setDeviceId(activeId);
    } catch (e) {
      // NotAllowedError = refus explicite ; NotFoundError = pas de caméra ;
      // le reste (caméra déjà occupée par une autre app, etc.) tombe dans le
      // message générique.
      if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
        setError(t('camera.error_denied'));
      } else if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') {
        setError(t('camera.error_no_device'));
      } else {
        setError(t('camera.error_generic'));
      }
    } finally {
      setStarting(false);
    }
  }, [stopStream, t]);

  // Démarrage unique au montage (deps vides volontairement : redémarrer le
  // flux à chaque rendu couperait la vidéo en pleine visée), arrêt des pistes
  // au démontage.
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(t('camera.error_unsupported'));
      setStarting(false);
      return;
    }
    start('');
    return stopStream;
  }, []); // eslint-disable-line

  // Fermeture au clavier : une modale plein écran sans Échap est un piège.
  // Réenregistré quand `shot` change : handleClose lit ce cliché en attente
  // pour révoquer son URL objet, un handler figé au montage fuirait le blob.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shot]); // eslint-disable-line

  const handleClose = () => {
    stopStream();
    if (shot?.url) URL.revokeObjectURL(shot.url);
    onClose();
  };

  const handleSwitchCamera = (nextId) => {
    setDeviceId(nextId);
    start(nextId);
  };

  const handleShoot = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    // drawImage sans transformation : le fichier produit correspond exactement
    // à ce que voit le capteur (cf. avertissement sur le miroir plus haut).
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setShot({ file, url: URL.createObjectURL(blob) });
    }, 'image/jpeg', 0.92);
  };

  const handleRetake = () => {
    if (shot?.url) URL.revokeObjectURL(shot.url);
    setShot(null);
  };

  const handleConfirm = () => {
    if (!shot) return;
    stopStream();
    // Le parent reçoit un File : strictement interchangeable avec celui d'un
    // <input type="file">, donc tout le pipeline existant (compressImage puis
    // POST) fonctionne sans modification.
    onCapture(shot.file);
    if (shot.url) URL.revokeObjectURL(shot.url);
    onClose();
  };

  return (
    <div className="cam-backdrop" onClick={handleClose}>
      <div className="cam-modal" onClick={e => e.stopPropagation()}>
        <div className="cam-header">
          <span className="cam-title">{label || t('camera.title')}</span>
          <button className="cam-close" onClick={handleClose} aria-label={t('camera.close')}>×</button>
        </div>

        <div className="cam-stage">
          {error ? (
            <div className="cam-error">
              <p>{error}</p>
              <button className="btn-secondary" onClick={() => start(deviceId)}>{t('camera.retry')}</button>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                className="cam-video"
                playsInline
                muted
                style={{ display: shot ? 'none' : 'block' }}
              />
              {shot && <img className="cam-video" src={shot.url} alt={t('camera.preview_alt')} />}
              {starting && !shot && (
                <div className="cam-starting"><span className="dot-pulse" /> {t('camera.starting')}</div>
              )}
            </>
          )}
        </div>

        {!error && (
          <div className="cam-actions">
            {devices.length > 1 && !shot && (
              <select className="cam-device" value={deviceId}
                onChange={e => handleSwitchCamera(e.target.value)}>
                {devices.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || t('camera.device_fallback', { n: i + 1 })}
                  </option>
                ))}
              </select>
            )}
            {shot ? (
              <>
                <button className="btn-ghost" onClick={handleRetake}>{t('camera.retake')}</button>
                <button className="btn-primary" onClick={handleConfirm}>{t('camera.use')}</button>
              </>
            ) : (
              <button className="btn-primary cam-shoot" onClick={handleShoot} disabled={starting}>
                {t('camera.shoot')}
              </button>
            )}
          </div>
        )}

        <p className="cam-hint">{t('camera.no_mirror_hint')}</p>
      </div>
    </div>
  );
}
